import {
  Component,
  ElementRef,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  NgZone,
  DoCheck
} from '@angular/core';
import { Chart, ChartType, ChartData, ChartOptions, registerables, DefaultDataPoint } from 'chart.js';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(zoomPlugin, ...registerables);

export interface ChartConfig<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown> {
  type: TType;
  data: ChartData<TType, TData, TLabel>;
  options?: ChartOptions<TType>;
}

@Component({
  selector: 'app-analytics-chart',
  templateUrl: './analytics-chart.component.html',
  styleUrls: ['./analytics-chart.component.scss']
})
export class AnalyticsChartComponent implements AfterViewInit, OnChanges, DoCheck, OnDestroy {
  @Input() data: any[] = [];
  @Input() descriptor: { label: string; values: string } = { label: '', values: '' };
  @Input() formatFn?: (item: any) => { label: string; value: number };
  @Input() chartType: 'bar' | 'line' | 'pie' | 'doughnut' = 'bar';
  @Input() datasetLabel = 'Dataset';
  @Input() analyticsResults?: any[];
  @Input() resultsLabelsKey = 'month';
  @Input() resultsValueKey = 'mission_count';
  @Input() datasetLabels: string[] = [];
  @Input() colors: string[] = [];
  @Input() showLegend = true;

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartContainer') chartContainer!: ElementRef<HTMLDivElement>;

  private chart: Chart | null = null;
  private dataChanges$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private previousAnalyticsResultsLength?: number;
  private checkInProgress = false;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit() {
    this.dataChanges$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.updateChart();
    });

    this.updateChart();
  }

  ngDoCheck() {
    if (this.checkInProgress || !this.analyticsResults || !Array.isArray(this.analyticsResults)) {
      return;
    }

    this.checkInProgress = true;

    try {
      const currentLength = this.analyticsResults.length;
      if (this.previousAnalyticsResultsLength !== currentLength) {
        this.previousAnalyticsResultsLength = currentLength;
        this.preserveContainerHeight();
        this.dataChanges$.next();
      }
    } finally {
      this.checkInProgress = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['data'] ||
      changes['descriptor'] ||
      changes['formatFn'] ||
      changes['chartType'] ||
      changes['datasetLabel'] ||
      changes['analyticsResults'] ||
      changes['resultsLabelsKey'] ||
      changes['resultsValueKey'] ||
      changes['datasetLabels'] ||
      changes['colors'] ||
      changes['showLegend']
    ) {
      this.preserveContainerHeight();
      this.dataChanges$.next();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  public toggleDataset(datasetIndex: number): void {
    if (this.chart && this.chart.data.datasets[datasetIndex]) {
      const visible = this.chart.isDatasetVisible(datasetIndex);
      this.chart.setDatasetVisibility(datasetIndex, !visible);
      this.chart.update();
    }
  }

  public showDataset(datasetIndex: number): void {
    if (this.chart && this.chart.data.datasets[datasetIndex]) {
      this.chart.setDatasetVisibility(datasetIndex, true);
      this.chart.update();
    }
  }

  public hideDataset(datasetIndex: number): void {
    if (this.chart && this.chart.data.datasets[datasetIndex]) {
      this.chart.setDatasetVisibility(datasetIndex, false);
      this.chart.update();
    }
  }

  public isDatasetVisible(datasetIndex: number): boolean {
    if (this.chart && this.chart.data.datasets[datasetIndex]) {
      return this.chart.isDatasetVisible(datasetIndex);
    }
    return true;
  }

  public showAllDatasets(): void {
    if (this.chart) {
      this.chart.data.datasets.forEach((_, index) => {
        this.chart!.setDatasetVisibility(index, true);
      });
      this.chart.update();
    }
  }

  public hideAllDatasets(): void {
    if (this.chart) {
      this.chart.data.datasets.forEach((_, index) => {
        this.chart!.setDatasetVisibility(index, false);
      });
      this.chart.update();
    }
  }

  public forceUpdate(): void {
    this.preserveContainerHeight();
    this.dataChanges$.next();
  }

  private updateChart() {
    if (!this.chartCanvas) {
      return;
    }

    const formattedData = this.formatChartData();

    this.ngZone.runOutsideAngular(() => {
      if (this.chart) {
        // Collect current visibility from existing chart
        const datasetVisibility = this.chart.data.datasets.map((_, i) => this.chart!.isDatasetVisible(i));

        this.chart.data = formattedData.data;

        // Re-apply visibility to new datasets
        this.chart.data.datasets.forEach((ds: any, i: number) => {
          if (i < datasetVisibility.length) {
            ds.hidden = !datasetVisibility[i];
          }
        });

        this.chart.options = formattedData.options || {};
        this.chart.update('none');
        setTimeout(() => this.clearTemporaryMinHeight(), 300);
      } else {
        const ctx = this.chartCanvas.nativeElement.getContext('2d');
        if (ctx) {
          this.chart = new Chart(ctx, {
            type: this.chartType,
            data: formattedData.data,
            options: formattedData.options || {
              responsive: true,
              maintainAspectRatio: false
            }
          });
          setTimeout(() => this.clearTemporaryMinHeight(), 300);
        }
      }
    });
  }

  private preserveContainerHeight(): void {
    try {
      if (this.chartContainer && this.chartContainer.nativeElement) {
        const h = Math.round(this.chartContainer.nativeElement.getBoundingClientRect().height);
        if (h > 0) {
          this.chartContainer.nativeElement.style.minHeight = `${h}px`;
        }
      }
    } catch {
      /* noop */
    }
  }

  private clearTemporaryMinHeight(): void {
    try {
      if (this.chartContainer && this.chartContainer.nativeElement) {
        this.chartContainer.nativeElement.style.minHeight = '';
      }
    } catch {
      /* noop */
    }
  }

  private formatChartData(): ChartConfig {
    if (
      (this.analyticsResults == null || this.analyticsResults.length === 0) &&
      (!this.data || this.data.length === 0)
    ) {
      return {
        type: this.chartType,
        data: {
          labels: [],
          datasets: [
            {
              label: this.datasetLabel,
              data: []
            }
          ]
        },
        options: this.getChartOptions()
      };
    }

    if (this.analyticsResults && this.analyticsResults.length > 0) {
      const labelSet = new Set<string>();
      this.analyticsResults.forEach((res) => {
        if (res && Array.isArray(res.rows)) {
          res.rows.forEach((r: any) => {
            const lbl = String(r[this.resultsLabelsKey]);
            labelSet.add(lbl);
          });
        }
      });

      const labels = Array.from(labelSet).sort();

      const datasets = this.analyticsResults.map((res, idx) => {
        const map: Record<string, number> = {};
        if (res && Array.isArray(res.rows)) {
          res.rows.forEach((r: any) => {
            const lbl = String(r[this.resultsLabelsKey]);
            map[lbl] = Number(r[this.resultsValueKey]) || 0;
          });
        }

        const data = labels.map((lbl) => (map[lbl] != null ? map[lbl] : 0));

        const color = this.colors && this.colors[idx] ? this.colors[idx] : undefined;

        const dataset: any = {
          label: this.datasetLabels && this.datasetLabels[idx] ? this.datasetLabels[idx] : `Series ${idx + 1}`,
          data,
          borderWidth: 2,
          fill: false,
          tension: 0,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 0
        };

        if (color) {
          dataset.backgroundColor = color;
          dataset.borderColor = color;
          dataset.pointBackgroundColor = color;
        }

        return dataset;
      });

      return {
        type: this.chartType,
        data: {
          labels,
          datasets
        },
        options: this.getChartOptions()
      };
    }

    let labels: string[] = [];
    let values: number[] = [];

    if (this.formatFn) {
      this.data.forEach((item) => {
        const formatted = this.formatFn!(item);
        labels.push(formatted.label);
        values.push(formatted.value);
      });
    } else {
      labels = this.data.map((item) => String(item[this.descriptor.label]));
      values = this.data.map((item) => Number(item[this.descriptor.values]));
    }

    if (this.chartType === 'pie' || this.chartType === 'doughnut') {
      const dataset: any = {
        label: this.datasetLabel,
        data: values,
        borderWidth: 1
      };

      if (this.colors && this.colors.length > 0) {
        dataset.backgroundColor = this.colors;
        dataset.borderColor = this.colors;
      }

      return {
        type: this.chartType,
        data: {
          labels,
          datasets: [dataset]
        },
        options: this.getChartOptions()
      };
    }

    const isLine = this.chartType === 'line';
    const color = this.colors && this.colors.length > 0 ? this.colors[0] : undefined;

    const dataset: any = {
      label: this.datasetLabel,
      data: values,
      borderWidth: isLine ? 2 : 1,
      fill: false,
      tension: 0
    };

    if (color) {
      dataset.backgroundColor = color;
      dataset.borderColor = color;
    }

    if (isLine) {
      dataset.pointRadius = 6;
      dataset.pointHoverRadius = 8;
      dataset.pointBorderWidth = 0;
      if (color) {
        dataset.pointBackgroundColor = color;
      }
    }

    return {
      type: this.chartType,
      data: {
        labels,
        datasets: [dataset]
      },
      options: this.getChartOptions()
    };
  }

  private getChartOptions(): ChartOptions {
    const isLine = this.chartType === 'line';

    const options: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 200
      },
      plugins: {
        legend: {
          display: this.showLegend,
          position: 'top',
          align: 'start',
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            padding: 20,
            boxWidth: 50,
            boxHeight: 20,
            font: {
              size: 8
            }
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 4,
          displayColors: true
        },

        zoom: {
          pan: {
            enabled: true
          },
          zoom: {
            wheel: {
              enabled: true
            },
            pinch: {
              enabled: true
            },

            mode: 'xy'
          },
          limits: {
            y: { min: 'original', max: 'original', minRange: 1 }
          }
        }
      }
    };

    if (isLine) {
      (options as any).scales = {
        x: {
          grid: {
            display: true,
            drawBorder: true,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 12
            }
          }
        },
        y: {
          grid: {
            display: true,
            drawBorder: true,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 12
            }
          }
        }
      };
    }

    return options;
  }
}
